import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import app from '../../services/firebase';
import TopNavbar from '../TopNavbar/TopNavbar';
import '../../styles/App.css';

interface UserStats {
  username: string;
  caloriesBurned: number;
  steps: number;
}

const MainPage = () => {
  const navigate = useNavigate();
  const auth = getAuth(app);
  const db = getFirestore(app);

  const [leaderboardData, setLeaderboardData] = useState<UserStats[]>([]);
  const [activeTab, setActiveTab] = useState<'calories' | 'steps'>('calories');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Redirect to login if the user is not authenticated
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const checkAndResetMonthlyData = async () => {
      try {
        const appConfigRef = doc(db, 'appConfig', 'monthlyReset');
        const appConfigSnapshot = await getDoc(appConfigRef);

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        if (appConfigSnapshot.exists()) {
          const lastReset = appConfigSnapshot.data()?.lastReset?.toDate();
          const lastResetMonth = lastReset?.getMonth();
          const lastResetYear = lastReset?.getFullYear();

          // If the last reset was in a different month or year, perform the reset
          if (lastResetMonth !== currentMonth || lastResetYear !== currentYear) {
            await resetMonthlyData();
            await setDoc(appConfigRef, { lastReset: Timestamp.fromDate(now) });
          }
        } else {
          // If no reset has ever been performed, initialize the reset date
          console.log('No reset has ever been performed. Initializing...');
          await resetMonthlyData();
          await setDoc(appConfigRef, { lastReset: Timestamp.fromDate(now) });
        }
      } catch (err) {
        console.error('Error checking or resetting monthly data:', err);
      }
    };

    const resetMonthlyData = async () => {
      try {
        const monthlyDataRef = collection(db, 'monthlyData');
        const metricDocs = await getDocs(monthlyDataRef);

        for (const metricDoc of metricDocs.docs) {
          const userTotals = metricDoc.data() as Record<string, { total: number }>;

          // Reset all user totals to 0
          const resetTotals = Object.keys(userTotals).reduce((acc, userId) => {
            acc[userId] = { total: 0 };
            return acc;
          }, {} as Record<string, { total: number }>);

          // Update the document with reset totals
          await setDoc(metricDoc.ref, resetTotals);
        }

        console.log('Monthly data reset successfully.');
      } catch (err) {
        console.error('Error resetting monthly data:', err);
      }
    };

    checkAndResetMonthlyData();
  }, [db]);

  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        const metric = activeTab === 'calories' ? 'caloriesBurned' : 'stepsTaken';
        const metricDocRef = doc(db, 'monthlyData', metric);

        const metricSnapshot = await getDoc(metricDocRef);

        if (!metricSnapshot.exists()) {
          console.error(`Document for ${metric} does not exist.`);
          setLeaderboardData([]);
          return;
        }

        const userTotals = metricSnapshot.data() as Record<string, { total: number }>;

        // Fetch usernames for all user IDs
        const userStats: UserStats[] = await Promise.all(
          Object.entries(userTotals).map(async ([userId, data]) => {
            const userDocRef = doc(db, 'users', userId);
            const userDocSnapshot = await getDoc(userDocRef);

            const username = userDocSnapshot.exists() ? userDocSnapshot.data()?.username || 'Unknown' : 'Unknown';

            return {
              username,
              caloriesBurned: metric === 'caloriesBurned' ? data.total : 0,
              steps: metric === 'stepsTaken' ? data.total : 0,
            };
          })
        );

        // Sort the leaderboard data
        const sortedStats =
          activeTab === 'calories'
            ? userStats.sort((a, b) => b.caloriesBurned - a.caloriesBurned)
            : userStats.sort((a, b) => b.steps - a.steps);

        setLeaderboardData(sortedStats);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to fetch leaderboard data. Please try again.');
      }
    };

    fetchLeaderboardData();
  }, [activeTab, db]);

  return (
    <div>
      <TopNavbar />
      <div className="main-container">
        <h1 className="welcome-text">Welcome to SHRED</h1>

        {/* Error Message */}
        {error && <div className="error-bubble">{error}</div>}

        {/* Leaderboard Tabs */}
        <div className="leaderboard-tabs">
          <button
            className={activeTab === 'calories' ? 'active-tab' : ''}
            onClick={() => setActiveTab('calories')}
          >
            Calories Burned
          </button>
          <button
            className={activeTab === 'steps' ? 'active-tab' : ''}
            onClick={() => setActiveTab('steps')}
          >
            Steps Taken
          </button>
        </div>

        {/* Leaderboard */}
        <div className="leaderboard">
          <h2>
            {activeTab === 'calories'
              ? `Calories Burned Leaderboard - ${new Date().toLocaleString('default', { month: 'long' })}`
              : `Steps Taken Leaderboard - ${new Date().toLocaleString('default', { month: 'long' })}`}
          </h2>
          <ul>
            {leaderboardData.map((user, index) => (
              <li key={index} className="leaderboard-item">
                <span className="rank">{index + 1}.</span>
                <span className="username">{user.username}</span>
                <span className="metric">
                  {activeTab === 'calories' ? `${user.caloriesBurned} kcal` : `${user.steps} steps`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MainPage;

